import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationBarService } from '../notification/notification-bar.service';
import { LoginSubscriber } from 'shared/services/subscribers/login-subscriber.service';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';
import { BcAuth } from 'common/auth/auth.service';
import { NotificationType } from 'common/common/constants/notification-type.enum';
import { StreamService } from 'common/dynamicquery/stream.service';

@Injectable()
export class StreamStatusService {
  public timer;
  public firstConnecting;
  public isReconnecting;
  private _logoutSubscription: Subscription;
  private _loginSubscription: Subscription;
  private _removeNotificationTimeout;


  constructor(
    private _bcNotificationService: NotificationBarService,
    private _loginSubscriber: LoginSubscriber,
    private _logoutSubscriber: LogoutSubscriber,
    public auth: BcAuth,
    private _stream: StreamService) {
      this.timer = null;
      this.firstConnecting = true;
      this.isReconnecting = false;
  }

  public init(): void {
    this._stream.addListener(this._listener.bind(this));
    this._logoutSubscription = this._logoutSubscriber.subscribe(() => this._onLoginStateChange());
    this._loginSubscription = this._loginSubscriber.subscribe(() => this._onLoginStateChange());
  }

  public destroy(): void {
    this._stream.removeListener(this._listener.bind(this));

    if (this._logoutSubscription) {
      this._logoutSubscription.unsubscribe();
      this._logoutSubscription = null;
    }

    if (this._loginSubscription) {
      this._loginSubscription.unsubscribe();
      this._loginSubscription = null;
    }

    if (this._removeNotificationTimeout) {
      clearTimeout(this._removeNotificationTimeout);
    }
  }

  private _onLoginStateChange(): void {
    this._action();
  }

  private _listener(): void {
    const isConnected = this._stream.isConnected();
    if (this.timer && !isConnected) {
      setTimeout(() => {
        clearTimeout(this.timer);
      }, this.timer);
      this.timer = null;
    }
    if (!isConnected) {
      this._action();
    } else {
      // This is to avoid frequent blinking of the reconnecting overlay on an unstable connection
      this.timer = setTimeout(() => {
        this._action();
        this.timer = null;
      }, 1000);
    }
  }

  private _action(): void {
    this.timer = null;
    const isConnected = this._stream.isConnected();
    if (this.firstConnecting && !isConnected) {
      this.isReconnecting = true;
      this._bcNotificationService.addNotification({ id: 'stream-reconnecting', content: 'bcStreamStatus.reconnecting', isClosable: false });
      this.firstConnecting = false;
    } else if (!this.firstConnecting) {
      this.isReconnecting = !isConnected;

      if (this.isReconnecting && this.auth.isLoggedIn()) {
        this._bcNotificationService.removeNotification('stream-reconnected');
        this._bcNotificationService.addNotification({ id: 'stream-reconnecting', content: 'bcStreamStatus.reconnecting', isClosable: false });
      } else if (!this.isReconnecting && this.auth.isLoggedIn()) {
        this._reconnected();
      } else {
        this._bcNotificationService.removeNotification('stream-reconnected');
        this._bcNotificationService.removeNotification('stream-reconnecting');
      }
    }
  }

  private _reconnected(): void {
    this._bcNotificationService.removeNotification('stream-reconnecting');
    this._bcNotificationService.addNotification({ id: 'stream-reconnected', content: 'bcStreamStatus.reconnected', type: NotificationType.Success, isClosable: true, autoCloseMillis: 2000 });
  }
}
